<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useVuelidate } from '@vuelidate/core';
import { required, url, minLength } from '@vuelidate/validators';
import wootConstants from 'dashboard/constants/globals';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { buildWebhookEventGroups } from './webhookEvents';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  value: { type: Object, default: () => ({}) },
});

const { EXAMPLE_WEBHOOK_URL } = wootConstants;
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { inboxEventsEnabled } = useConfig();

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const webhookUrl = ref(props.value.url || '');
const name = ref(props.value.name || '');
const subscriptions = ref([...(props.value.subscriptions || [])]);
const secretVisible = ref(false);

const rules = {
  webhookUrl: { required, minLength: minLength(7), url },
  subscriptions: { required },
};
const v$ = useVuelidate(rules, { webhookUrl, subscriptions });

const hasProposalPipeline = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE
  )
);

const eventGroups = computed(() =>
  buildWebhookEventGroups({
    inboxEvents: inboxEventsEnabled,
    proposalPipeline: hasProposalPipeline.value,
  })
);

const hasSecret = computed(() => !!props.value.secret);
const isValid = computed(() => !v$.value.$invalid);

const eventLabel = event =>
  t(
    getI18nKey('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS', event)
  );

const groupState = group => {
  const selected = group.events.filter(event =>
    subscriptions.value.includes(event)
  );
  return {
    all: selected.length === group.events.length,
    some: selected.length > 0 && selected.length < group.events.length,
  };
};

const toggleGroup = group => {
  const { all } = groupState(group);
  const rest = subscriptions.value.filter(
    event => !group.events.includes(event)
  );
  subscriptions.value = all ? rest : [...rest, ...group.events];
};

const toggleEvent = event => {
  subscriptions.value = subscriptions.value.includes(event)
    ? subscriptions.value.filter(item => item !== event)
    : [...subscriptions.value, event];
};

const copySecret = async () => {
  await copyTextToClipboard(props.value.secret);
  useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY_SUCCESS'));
};

defineExpose({
  isValid,
  payload: computed(() => ({
    url: webhookUrl.value,
    name: name.value,
    subscriptions: subscriptions.value,
  })),
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-4">
      <Input
        v-model="webhookUrl"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.LABEL')"
        :placeholder="
          $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.PLACEHOLDER', {
            webhookExampleURL: EXAMPLE_WEBHOOK_URL,
          })
        "
        :message="
          v$.webhookUrl.$error
            ? $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.ERROR')
            : ''
        "
        :message-type="v$.webhookUrl.$error ? 'error' : 'info'"
        @blur="v$.webhookUrl.$touch"
      />
      <Input
        v-model="name"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.LABEL')"
        :placeholder="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.PLACEHOLDER')"
      />
      <div v-if="hasSecret" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.LABEL') }}
        </label>
        <div class="flex items-center gap-2">
          <div class="flex-1 min-w-0">
            <Input
              :model-value="
                secretVisible
                  ? value.secret
                  : '••••••••••••••••••••••••••••••••'
              "
              readonly
              custom-input-class="font-mono"
            />
          </div>
          <NextButton
            v-if="isAdmin"
            v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.TOGGLE')"
            type="button"
            :icon="secretVisible ? 'i-lucide-eye-off' : 'i-lucide-eye'"
            slate
            faded
            @click="secretVisible = !secretVisible"
          />
          <NextButton
            v-if="isAdmin"
            v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY')"
            type="button"
            icon="i-lucide-copy"
            slate
            faded
            @click="copySecret"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.LABEL') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.HELP') }}
        </span>
      </div>

      <div
        v-for="group in eventGroups"
        :key="group.key"
        class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-2">
          <Checkbox
            :model-value="groupState(group).all"
            :indeterminate="groupState(group).some"
            @change="toggleGroup(group)"
          />
          <Icon :icon="group.icon" class="size-4 text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{
              $t(
                `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.GROUPS.${group.key}`
              )
            }}
          </span>
        </div>
        <div class="grid gap-2 ltr:pl-6 rtl:pr-6 sm:grid-cols-2">
          <label
            v-for="event in group.events"
            :key="event"
            class="flex items-start gap-2 cursor-pointer"
          >
            <Checkbox
              :model-value="subscriptions.includes(event)"
              class="mt-0.5"
              @change="toggleEvent(event)"
            />
            <span class="flex flex-col min-w-0">
              <span class="text-sm text-n-slate-12">{{
                eventLabel(event)
              }}</span>
              <span class="font-mono text-xs truncate text-n-slate-10">{{
                event
              }}</span>
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
